import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";

import { supportTicketService } from "../services/adminSupportTicketService";

const SupportTicket = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [searchQuery, setSearchQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const fetchTickets = async () => {
    setSuccessMessage("");
    setErrorMessage("");
    try {
      setLoading(true);
      const response = await supportTicketService.getAllTickets();
      setTickets(Array.isArray(response?.data) ? response.data : []);
      console.log("Admin tickets fetched:", response?.data);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleViewTicket = (ticketId) => {
    const ticket = tickets.find((item) => item.id === ticketId);
    if (!ticket) {
      setErrorMessage("Unable to find the selected ticket.");
      return;
    }

    setSelectedTicket(ticket);
    setIsViewDialogOpen(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleEditTicket = (ticketId) => {
    const ticket = tickets.find((item) => item.id === ticketId);
    if (!ticket) {
      setErrorMessage("Unable to find the selected ticket.");
      return;
    }

    setSelectedTicket(ticket);
    setEditStatus(ticket.status || "open");
    setIsEditDialogOpen(true);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to delete this support ticket?")) {
      return;
    }

    try {
      setErrorMessage("");
      setSuccessMessage("");
      const response = await supportTicketService.deleteTicket(ticketId);

      if (!response.success) {
        throw new Error(response.message || "Failed to delete ticket.");
      }

      setTickets((prevTickets) =>
        prevTickets.filter((ticket) => ticket.id !== ticketId),
      );
      setSuccessMessage("Support ticket deleted successfully.");
    } catch (error) {
      console.error("Error deleting ticket:", error);
      setErrorMessage(
        error?.message || "Unable to delete the ticket. Please try again.",
      );
    }
  };

  const handleUpdateTicketStatus = async () => {
    if (!selectedTicket) {
      setErrorMessage("No ticket selected for update.");
      return;
    }

    try {
      setErrorMessage("");
      setSuccessMessage("");
      setIsStatusUpdating(true);

      const response = await supportTicketService.updateTicketStatus(
        selectedTicket.id,
        editStatus,
      );

      if (!response.success) {
        throw new Error(response.message || "Failed to update ticket status.");
      }

      const updatedTicket = {
        ...selectedTicket,
        status: editStatus,
      };

      setTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket.id === selectedTicket.id ? updatedTicket : ticket,
        ),
      );
      setSelectedTicket(updatedTicket);
      setSuccessMessage("Ticket status updated successfully.");
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error("Error updating ticket status:", error);
      setErrorMessage(
        error?.message || "Unable to update the ticket. Please try again.",
      );
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    const subject = (ticket.subject || "").toLowerCase();
    const customerName = (ticket.customerName || "").toLowerCase();
    const ticketId = (ticket.ticketId || "").toLowerCase();
    const query = (searchQuery || "").toLowerCase();
    const matchesSearch =
      subject.includes(query) ||
      customerName.includes(query) ||
      ticketId.includes(query);
    const matchesPriority =
      priorityFilter === "all" || ticket.priority === priorityFilter;
    const matchesStatus =
      statusFilter === "all" || ticket.status === statusFilter;
    return matchesSearch && matchesPriority && matchesStatus;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "error";
      case "in-progress":
        return "warning";
      case "resolved":
        return "success";
      case "closed":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Support Ticket
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              alignItems: isMobile ? "stretch" : "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              placeholder="Search by Subject..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
                    <SearchIcon color="action" />
                  </Box>
                ),
              }}
              sx={{ minWidth: isMobile ? "100%" : 300, flexGrow: 1 }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <FormControl
                size="small"
                sx={{ minWidth: isMobile ? "100%" : 150 }}
              >
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(event) => setPriorityFilter(event.target.value)}
                  label="Priority"
                  startAdornment={<FilterIcon sx={{ mr: 1, fontSize: 20 }} />}
                >
                  <MenuItem value="all">All Priority</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: isMobile ? "100%" : 140 }}
              >
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={<FilterIcon />}
                sx={{ minWidth: 100, alignSelf: isMobile ? "stretch" : "auto" }}
              >
                Filter
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Loading tickets...
              </Typography>
            </Box>
          ) : filteredTickets.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                No support ticket found
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search or filter criteria
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box
              sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {filteredTickets.map((ticket) => (
                <Card
                  key={ticket.id}
                  sx={{ p: 2, border: "1px solid #e0e0e0" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                    >
                      #{ticket.ticketId}
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexDirection: "column" }}
                    >
                      <Chip
                        label={ticket.priority}
                        color={getPriorityColor(ticket.priority)}
                        size="small"
                        sx={{
                          fontSize: "0.75rem",
                          height: "24px",
                          textTransform: "capitalize",
                        }}
                      />
                      <Chip
                        label={ticket.status}
                        color={getStatusColor(ticket.status)}
                        size="small"
                        sx={{
                          fontSize: "0.75rem",
                          height: "24px",
                          textTransform: "capitalize",
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontSize: "0.8rem", fontWeight: 500 }}
                  >
                    {ticket.subject}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: "0.8rem" }}
                  >
                    {ticket.customerName}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 2, display: "block" }}
                  >
                    {ticket.email}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 2, display: "block" }}
                  >
                    Created:{" "}
                    {ticket.createdAt
                      ? new Date(ticket.createdAt).toLocaleDateString()
                      : "-"}
                  </Typography>

                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleViewTicket(ticket.id)}
                      title="View"
                      sx={{ p: 1 }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditTicket(ticket.id)}
                      title="Edit"
                      sx={{ p: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTicket(ticket.id)}
                      title="Delete"
                      color="error"
                      sx={{ p: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: 600, overflowX: "auto" }}
            >
              <Table stickyHeader sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Ticket Number</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>Subject</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Customer Name</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Email</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Priority</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Created At</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Last Updated</TableCell>
                    <TableCell sx={{ minWidth: 120 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell sx={{ minWidth: 120 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ticket.ticketId}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Typography variant="body2">
                          {ticket.subject}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="body2">
                          {ticket.customerName}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Typography variant="body2" color="text.secondary">
                          {ticket.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <Chip
                          label={ticket.priority}
                          color={getPriorityColor(ticket.priority)}
                          size="small"
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <Chip
                          label={ticket.status}
                          color={getStatusColor(ticket.status)}
                          size="small"
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="caption">
                          {ticket.createdAt
                            ? new Date(ticket.createdAt).toLocaleString()
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="caption">
                          {ticket.lastUpdated
                            ? new Date(ticket.lastUpdated).toLocaleString()
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }} align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleViewTicket(ticket.id)}
                            title="View"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditTicket(ticket.id)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTicket(ticket.id)}
                            title="Delete"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
        <Card
          sx={{
            minWidth: isMobile ? "100%" : 200,
            flexGrow: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transform: "translateY(-2px)",
              borderColor: "#1976d2",
            },
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 3 }}>
            <Typography
              variant="h4"
              color="primary"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {tickets.filter((t) => t.status === "open").length}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Open Tickets
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            minWidth: isMobile ? "100%" : 200,
            flexGrow: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transform: "translateY(-2px)",
              borderColor: "#2e7d32",
            },
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 3 }}>
            <Typography
              variant="h4"
              color="success.main"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {tickets.filter((t) => t.status === "closed").length}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Closed Tickets
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            minWidth: isMobile ? "100%" : 200,
            flexGrow: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transform: "translateY(-2px)",
              borderColor: "#ed6c02",
            },
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 3 }}>
            <Typography
              variant="h4"
              color="warning.main"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {
                tickets.filter(
                  (t) => t.priority === "urgent" || t.priority === "high",
                ).length
              }
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              High Priority
            </Typography>
          </CardContent>
        </Card>

        <Card
          sx={{
            minWidth: isMobile ? "100%" : 200,
            flexGrow: 1,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              transform: "translateY(-2px)",
              borderColor: "#0288d1",
            },
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 3 }}>
            <Typography
              variant="h4"
              color="info.main"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {tickets.length}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Total Tickets
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Support Ticket Details</DialogTitle>
        <DialogContent dividers>
          {selectedTicket ? (
            <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
              <Typography variant="subtitle2">Ticket Number</Typography>
              <Typography>{selectedTicket.ticketId}</Typography>

              <Typography variant="subtitle2">Subject</Typography>
              <Typography>{selectedTicket.subject}</Typography>

              <Typography variant="subtitle2">Category</Typography>
              <Typography sx={{ textTransform: "capitalize" }}>
                {selectedTicket.category}
              </Typography>

              <Typography variant="subtitle2">Priority</Typography>
              <Typography sx={{ textTransform: "capitalize" }}>
                {selectedTicket.priority}
              </Typography>

              <Typography variant="subtitle2">Status</Typography>
              <Typography sx={{ textTransform: "capitalize" }}>
                {selectedTicket.status}
              </Typography>

              <Typography variant="subtitle2">Customer</Typography>
              <Typography>{selectedTicket.customerName}</Typography>

              <Typography variant="subtitle2">Email</Typography>
              <Typography>{selectedTicket.email}</Typography>

              {selectedTicket.orderId && (
                <>
                  <Typography variant="subtitle2">Order ID</Typography>
                  <Typography>{selectedTicket.orderId}</Typography>
                </>
              )}

              <Typography variant="subtitle2">Description</Typography>
              <Typography>{selectedTicket.description}</Typography>
            </Box>
          ) : (
            <Typography>No ticket selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsViewDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsViewDialogOpen(false);
              if (selectedTicket) {
                handleEditTicket(selectedTicket.id);
              }
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Ticket Status</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            {selectedTicket ? (
              <>
                <Typography variant="subtitle2">Ticket Number</Typography>
                <Typography>{selectedTicket.ticketId}</Typography>

                <Typography variant="subtitle2">Current Status</Typography>
                <Typography sx={{ textTransform: "capitalize" }}>
                  {selectedTicket.status}
                </Typography>

                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editStatus}
                    label="Status"
                    onChange={(event) => setEditStatus(event.target.value)}
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
              </>
            ) : (
              <Typography>No ticket selected.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateTicketStatus}
            disabled={isStatusUpdating || !selectedTicket}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportTicket;
